// src/utils/fileDownload.js
/**
 * File download utilities for handling secure file downloads from S3
 *
 * Flow:
 * 1. User clicks file source
 * 2. Call API to get presigned URL (with auth)
 * 3. Auto-download from presigned URL (1h expiry)
 */

import { API_BASE_URL, getAccessToken } from './api';

/**
 * Download a file from content_url
 *
 * @param {string} contentUrl - S3 content URL (e.g., "s3://bucket/uploads/...")
 * @param {string} filename - Filename for download
 * @returns {Promise<void>}
 * @throws {Error} If download fails
 */
export async function downloadFile(contentUrl, filename) {
    try {
        // Step 1: Call download API to get presigned URL
        const token = getAccessToken();

        if (!token) {
            throw new Error('Bạn cần đăng nhập để tải file');
        }

        const response = await fetch(
            `${API_BASE_URL}/api/files/download?content_url=${encodeURIComponent(contentUrl)}`,
            {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            }
        );

        if (response.status === 403) {
            throw new Error('Bạn không có quyền tải file này');
        }

        if (response.status === 404) {
            throw new Error('File không tồn tại hoặc đã hết hạn');
        }

        if (!response.ok) {
            throw new Error(`Lỗi tải file: HTTP ${response.status}`);
        }

        const data = await response.json();
        // Response: { download_url, expires_in, filename, content_type }

        // Step 2: Auto-download from presigned URL
        const link = document.createElement('a');
        link.href = data.download_url;
        link.download = filename || data.filename || 'download';
        link.target = '_blank';

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log(`✅ Downloaded: ${data.filename}`);

    } catch (error) {
        console.error('Download failed:', error);
        throw error;
    }
}

/**
 * Open URL in new tab
 *
 * @param {string} url - URL to open
 */
export function openUrl(url) {
    window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * Handle source click based on action type
 *
 * @param {Object} source - Source object
 * @param {Function} onError - Error callback
 * @returns {Promise<void>}
 */
export async function handleSourceClick(source, onError) {
    try {
        if (source.metadata?.action === 'download') {
            // File source - download via API
            const filename = source.metadata.original_filename || source.title;
            await downloadFile(source.url, filename);
        } else if (source.metadata?.action === 'open' || !source.metadata?.action) {
            // URL source or legacy - open in new tab
            openUrl(source.url);
        } else {
            console.warn('Unknown source action:', source.metadata?.action);
            openUrl(source.url); // Fallback to opening URL
        }
    } catch (error) {
        if (onError) {
            onError(error);
        } else {
            // Default error handling
            alert(error.message || 'Không thể tải file. Vui lòng thử lại.');
        }
    }
}

/**
 * Get icon component name based on file type
 *
 * @param {Object} source - Source object
 * @returns {string} Icon type
 */
export function getSourceIconType(source) {
    // Check if file source
    if (source.type === 'file' && source.metadata?.file_type) {
        const fileType = source.metadata.file_type;

        if (fileType === 'application/pdf') {
            return 'pdf';
        }
        if (fileType.includes('word') || fileType.includes('document')) {
            return 'word';
        }
        if (fileType.includes('sheet') || fileType.includes('excel')) {
            return 'excel';
        }
        if (fileType.includes('presentation') || fileType.includes('powerpoint')) {
            return 'ppt';
        }
        if (fileType.startsWith('image/')) {
            return 'image';
        }

        return 'file';
    }

    // URL or search source
    return 'url';
}

/**
 * Format file size for display
 *
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted size
 */
export function formatFileSize(bytes) {
    if (!bytes) return '';

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex++;
    }

    return `${size.toFixed(size < 10 ? 1 : 0)}${units[unitIndex]}`;
}
